import * as React from "react";
import { useHistory } from "react-router-dom";
import { useQuery } from "react-query";
import { Typography, Box, Button, Alert, CircularProgress } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { ReportJob, MetricsService, ReportJobStatus, ReportFileInfo } from "gen/clients";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { formatDate } from "utils/dateUtils";
import { MtUsageReportTable } from "./MtUsageReportTable";
import { ExportMtUsageReportButton } from "./ExportMtUsageReportButton";
import { MtUsageReportFilter, MtUsageReportFilters } from "./MtUsageReportFilter";

interface Props {
  reportId: string;
}

const MtUsageForClientBillingReportDetailsPage: React.FC<Props> = ({ reportId }) => {
  const history = useHistory();
  const [filters, setFilters] = React.useState<MtUsageReportFilters>({});

  const handleFiltersChange = React.useCallback((newFilters: MtUsageReportFilters) => {
    setFilters(newFilters);
  }, []);

  const {
    data: report,
    isLoading,
    error
  } = useQuery<ReportJob>(["getMtUsageReport", reportId], {
    queryFn: () => MetricsService.getReportJob({ reportId }),
    refetchInterval: data => {
      return data?.status === ReportJobStatus.PENDING || data?.status === ReportJobStatus.RUNNING ? 5000 : false;
    }
  });

  const jsonFile = React.useMemo(() => {
    return report?.files?.find((file: ReportFileInfo) => file.format === ReportFileInfo.format.JSON);
  }, [report?.files]);

  const csvFile = React.useMemo(() => {
    return report?.files?.find((file: ReportFileInfo) => file.format === ReportFileInfo.format.CSV);
  }, [report?.files]);

  const handleBackClick = React.useCallback(() => {
    history.push("/analytics/mtUsageForClientBilling/reports");
  }, [history]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <ApiErrorMessage apiError={error} />;
  }

  if (!report) {
    return <Alert severity="error">Report not found</Alert>;
  }

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={handleBackClick} sx={{ mb: 2 }}>
            Back to Reports
          </Button>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              MT Usage Report - {reportId}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Status:</strong> {report.status}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Created by:</strong> {report.createdBy}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Created at:</strong> {formatDate(report.createdAt)}
            </Typography>
            {report.completedAt && (
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Completed at:</strong> {formatDate(report.completedAt)}
              </Typography>
            )}
            {report.params && (
              <>
                {report.params.fromDate && (
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>From:</strong> {formatDate(report.params.fromDate)}
                  </Typography>
                )}
                {report.params.toDate && (
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>To:</strong> {formatDate(report.params.toDate)}
                  </Typography>
                )}
                {report.params.billingType && (
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Billing Type:</strong> {report.params.billingType}
                  </Typography>
                )}
              </>
            )}
          </Box>

          {report.status === ReportJobStatus.COMPLETED && csvFile && (
            <ExportMtUsageReportButton reportId={reportId} csvFile={csvFile} />
          )}
        </Box>

        {report.status === ReportJobStatus.FAILED && report.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Error:</strong> {report.error.message}
            </Typography>
          </Alert>
        )}

        {(report.status === ReportJobStatus.PENDING || report.status === ReportJobStatus.RUNNING) && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Report is being generated. This page will automatically refresh when the report is ready.
            </Typography>
          </Alert>
        )}
      </Box>

      {report.status === ReportJobStatus.COMPLETED && jsonFile && jsonFile.s3Key && (
        <>
          <MtUsageReportFilter initialFilters={filters} onFiltersChange={handleFiltersChange} />
          <MtUsageReportTable reportId={reportId} jsonFile={jsonFile} filters={filters} />
        </>
      )}

      {report.status === ReportJobStatus.COMPLETED && (!jsonFile || !jsonFile.s3Key) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Report data is not available for viewing. The JSON file may not have been generated properly.
          </Typography>
        </Alert>
      )}
    </>
  );
};

export { MtUsageForClientBillingReportDetailsPage };
